import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import React, { FC, HTMLAttributes, ReactNode } from "react";
import styled, { keyframes } from "styled-components";


export type Order = "asc" | "desc";

// Interfaces
interface TableProps extends HTMLAttributes<HTMLTableElement> {
  children: ReactNode;
}

interface TableSectionProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode;
}

interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  children: ReactNode;
}

interface TableCellProps extends HTMLAttributes<HTMLTableCellElement> {
  children: ReactNode;
  colSpan?: number;
  rowSpan?: number;
  sortDirection?: Order | false;
}

interface TableHeadCellProps extends HTMLAttributes<HTMLTableCellElement> {
  children: ReactNode;
  colSpan?: number;
  rowSpan?: number;
}

interface TableSortLabelProps extends HTMLAttributes<HTMLDivElement> {
  active?: boolean;
  direction?: "asc" | "desc";
  children: ReactNode;
  onClick?: (e: any) => void;
}

const rotateAsc = keyframes`
  from {
    transform: rotate(180deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const rotateDesc = keyframes`
  from {
    transform: rotate(360deg);
  }
  to {
    transform: rotate(180deg);
  }
`;

const TableStyled = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  /* background-color: ${({ theme }) => theme.secondaryBackground}; */
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: ${({ theme }) => theme.evenRowBackground};
  }
  &:hover {
    background-color: ${({ theme }) => theme.hoverRowBackground};
  }
`;

const TableCell = styled.td`
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  font-size: 0.875rem;
  line-height: 1.5rem;
  color: ${({ theme }) => theme.text};
`;

const TableHeadCell = styled.th`
  padding: 16px;
  text-align: left;
  border-bottom: 2px solid ${({ theme }) => theme.border};
  font-size: 1rem;
  line-height: 1.5rem;
  color: ${({ theme }) => theme.text};
  font-weight: bold;
  background-color: ${({ theme }) => theme.secondaryBackground};
`;

const SortLabelWrapper = styled.div<{
  $active?: boolean;
  direction?: "asc" | "desc";
}>`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  font-weight: ${({ $active }) => ($active ? "bold" : "normal")};
  color: ${({ theme, $active }) =>
    $active ? theme.text : theme.secondaryText};
  font-weight: bold;
  transition: 0.2s;

  &:hover {
    color: ${({ theme, $active }) => ($active ? theme.text : theme.border)};
  }

  svg {
    font-size: 1.5rem;
    margin-left: 4px;
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    animation: ${({ direction }) =>
        direction === "asc"
          ? rotateAsc
          : direction === "desc"
          ? rotateDesc
          : "none"}
      0.3s ease;
    animation-fill-mode: forwards;
    transition: 0.3s;
  }

  &:hover svg {
    opacity: 1;
    color: ${({ theme, $active }) => ($active ? theme.text : theme.border)};
  }
`;

const Table: FC<TableProps> & {
  Head: FC<TableSectionProps>;
  Body: FC<TableSectionProps>;
  Row: FC<TableRowProps>;
  Cell: FC<TableCellProps>;
  HeadCell: FC<TableHeadCellProps>;
  SortLabel: FC<TableSortLabelProps>;
} = ({ children, ...props }) => {
  return <TableStyled {...props}>{children}</TableStyled>;
};

Table.Head = ({ children, ...props }: TableSectionProps) => (
  <TableHead {...props}>{children}</TableHead>
);

Table.Body = ({ children, ...props }: TableSectionProps) => (
  <TableBody {...props}>{children}</TableBody>
);

Table.Row = ({ children, ...props }: TableRowProps) => (
  <TableRow {...props}>{children}</TableRow>
);

Table.Cell = ({
  children,
  rowSpan,
  colSpan,
  sortDirection,
  ...props
}: TableCellProps) => (
  <TableCell
    colSpan={colSpan}
    rowSpan={rowSpan}
    aria-sort={
      sortDirection === "asc"
        ? "ascending"
        : sortDirection === "desc"
        ? "descending"
        : undefined
    }
    {...props}
  >
    {children}
  </TableCell>
);

Table.HeadCell = ({
  children,
  rowSpan,
  colSpan,
  ...props
}: TableHeadCellProps) => (
  <TableHeadCell colSpan={colSpan} rowSpan={rowSpan} {...props}>
    {children}
  </TableHeadCell>
);

Table.SortLabel = ({
  active,
  direction = "asc",
  children,
  onClick,
  ...props
}: TableSortLabelProps) => (
  <SortLabelWrapper
    $active={active}
    direction={direction}
    onClick={onClick}
    {...props}
  >
    {children}
    {<ArrowUpward />}
  </SortLabelWrapper>
);

export default Table;
